import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import axios from 'axios'
import { workspaceApi } from '../../api/workspaceApi'
import type {
  CreateDocumentRequest,
  CreateFolderRequest,
  DocumentDto,
  DocumentSummaryDto,
  FileMetadataDto,
  FolderDto,
  UpdateDocumentRequest,
  UpdateFolderRequest,
  WorkspaceState,
} from '../../Models/Workspace'

function errorMessage(e: unknown, fallback: string): string {
  if (axios.isAxiosError(e)) {
    const data = e.response?.data as { error?: string } | undefined
    return data?.error ?? e.message ?? fallback
  }
  if (e instanceof Error) return e.message
  return fallback
}

// ─── Folder thunks ───────────────────────────────────────────────────────────

export const fetchFolders = createAsyncThunk<FolderDto[], void, { rejectValue: unknown }>(
  'workspace/fetchFolders',
  async (_, thunkAPI) => {
    try {
      return await workspaceApi.getFolders()
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  }
)

export const createFolder = createAsyncThunk<FolderDto, CreateFolderRequest, { rejectValue: unknown }>(
  'workspace/createFolder',
  async (body, thunkAPI) => {
    try {
      return await workspaceApi.createFolder(body)
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  }
)

export const updateFolder = createAsyncThunk<
  FolderDto,
  { id: string; patch: UpdateFolderRequest },
  { rejectValue: unknown }
>('workspace/updateFolder', async ({ id, patch }, thunkAPI) => {
  try {
    return await workspaceApi.updateFolder(id, patch)
  } catch (error) {
    return thunkAPI.rejectWithValue(error)
  }
})

export const deleteFolder = createAsyncThunk<string, string, { rejectValue: unknown }>(
  'workspace/deleteFolder',
  async (id, thunkAPI) => {
    try {
      await workspaceApi.deleteFolder(id)
      return id
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  }
)

// ─── Document thunks ─────────────────────────────────────────────────────────

export const fetchDocuments = createAsyncThunk<
  DocumentSummaryDto[],
  { folderId?: string; taskId?: string } | void,
  { rejectValue: unknown }
>('workspace/fetchDocuments', async (params, thunkAPI) => {
  try {
    return await workspaceApi.getDocuments(params ?? undefined)
  } catch (error) {
    return thunkAPI.rejectWithValue(error)
  }
})

export const fetchDocument = createAsyncThunk<DocumentDto, string, { rejectValue: unknown }>(
  'workspace/fetchDocument',
  async (id, thunkAPI) => {
    try {
      return await workspaceApi.getDocument(id)
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  }
)

export const createDocument = createAsyncThunk<
  DocumentDto,
  CreateDocumentRequest,
  { rejectValue: unknown }
>('workspace/createDocument', async (body, thunkAPI) => {
  try {
    return await workspaceApi.createDocument(body)
  } catch (error) {
    return thunkAPI.rejectWithValue(error)
  }
})

export const updateDocument = createAsyncThunk<
  DocumentDto,
  { id: string; patch: UpdateDocumentRequest },
  { rejectValue: unknown }
>('workspace/updateDocument', async ({ id, patch }, thunkAPI) => {
  try {
    return await workspaceApi.updateDocument(id, patch)
  } catch (error) {
    return thunkAPI.rejectWithValue(error)
  }
})

export const deleteDocument = createAsyncThunk<string, string, { rejectValue: unknown }>(
  'workspace/deleteDocument',
  async (id, thunkAPI) => {
    try {
      await workspaceApi.deleteDocument(id)
      return id
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  }
)

// ─── File thunks ─────────────────────────────────────────────────────────────

export const fetchFiles = createAsyncThunk<
  FileMetadataDto[],
  { folderId?: string; documentId?: string; taskId?: string } | void,
  { rejectValue: unknown }
>('workspace/fetchFiles', async (params, thunkAPI) => {
  try {
    return await workspaceApi.getFiles(params ?? undefined)
  } catch (error) {
    return thunkAPI.rejectWithValue(error)
  }
})

export const uploadFile = createAsyncThunk<FileMetadataDto, FormData, { rejectValue: unknown }>(
  'workspace/uploadFile',
  async (formData, thunkAPI) => {
    try {
      return await workspaceApi.uploadFile(formData)
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  }
)

export const deleteFile = createAsyncThunk<string, string, { rejectValue: unknown }>(
  'workspace/deleteFile',
  async (id, thunkAPI) => {
    try {
      await workspaceApi.deleteFile(id)
      return id
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  }
)

// ─── Slice ────────────────────────────────────────────────────────────────────

const initialState: WorkspaceState = {
  folders: [],
  documents: [],
  openDocuments: {},
  files: [],
  foldersStatus: 'idle',
  documentsStatus: 'idle',
  filesStatus: 'idle',
}

const workspaceSlice = createSlice({
  name: 'workspace',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    // Folders
    builder
      .addCase(fetchFolders.pending, (state) => {
        state.foldersStatus = 'loading'
        state.error = undefined
      })
      .addCase(fetchFolders.fulfilled, (state, action) => {
        state.foldersStatus = 'succeeded'
        state.folders = action.payload
      })
      .addCase(fetchFolders.rejected, (state, action) => {
        state.foldersStatus = 'failed'
        state.error = errorMessage(action.payload ?? action.error, 'Failed to load folders.')
      })
      .addCase(createFolder.fulfilled, (state, action) => {
        state.folders.push(action.payload)
      })
      .addCase(updateFolder.fulfilled, (state, action) => {
        const idx = state.folders.findIndex((f) => f.id === action.payload.id)
        if (idx >= 0) state.folders[idx] = action.payload
      })
      .addCase(deleteFolder.fulfilled, (state, action) => {
        state.folders = state.folders.filter((f) => f.id !== action.payload)
      })

    // Documents
    builder
      .addCase(fetchDocuments.pending, (state) => {
        state.documentsStatus = 'loading'
        state.error = undefined
      })
      .addCase(fetchDocuments.fulfilled, (state, action) => {
        state.documentsStatus = 'succeeded'
        state.documents = action.payload
      })
      .addCase(fetchDocuments.rejected, (state, action) => {
        state.documentsStatus = 'failed'
        state.error = errorMessage(action.payload ?? action.error, 'Failed to load documents.')
      })
      .addCase(fetchDocument.fulfilled, (state, action) => {
        state.openDocuments[action.payload.id] = action.payload
        const idx = state.documents.findIndex((d) => d.id === action.payload.id)
        if (idx < 0) {
          state.documents.push(action.payload)
        }
      })
      .addCase(createDocument.fulfilled, (state, action) => {
        state.documents.unshift(action.payload)
        state.openDocuments[action.payload.id] = action.payload
      })
      .addCase(updateDocument.fulfilled, (state, action) => {
        state.openDocuments[action.payload.id] = action.payload
        const idx = state.documents.findIndex((d) => d.id === action.payload.id)
        if (idx >= 0) {
          state.documents[idx] = { ...state.documents[idx], ...action.payload }
        }
      })
      .addCase(deleteDocument.fulfilled, (state, action) => {
        state.documents = state.documents.filter((d) => d.id !== action.payload)
        delete state.openDocuments[action.payload]
      })

    // Files
    builder
      .addCase(fetchFiles.pending, (state) => {
        state.filesStatus = 'loading'
      })
      .addCase(fetchFiles.fulfilled, (state, action) => {
        state.filesStatus = 'succeeded'
        state.files = action.payload
      })
      .addCase(fetchFiles.rejected, (state, action) => {
        state.filesStatus = 'failed'
        state.error = errorMessage(action.payload ?? action.error, 'Failed to load files.')
      })
      .addCase(uploadFile.fulfilled, (state, action) => {
        state.files.unshift(action.payload)
      })
      .addCase(deleteFile.fulfilled, (state, action) => {
        state.files = state.files.filter((f) => f.id !== action.payload)
      })
  },
})

export default workspaceSlice.reducer
